import type { KkmServerCommand, KkmServerProtocolProfile, KkmServerTimeoutClass } from '@/shared/api';
import type {
  OrderFiscalExecutionTarget,
  OrderFiscalReceiptItem,
  OrderFiscalReceiptPlan,
  OrderFiscalRefundPayment,
  OrderFiscalRefundReceiptPlan,
} from '@shared/api/graphql/generated';

import { KKM_SERVER_2_3_16_36_PROFILE, KkmServerAdapterError } from '@/shared/api';
import {
  OrderCurrency,
  OrderFiscalOperationType,
  OrderFiscalPaymentMethod,
  OrderFiscalPaymentSubject,
  OrderFiscalReceiptKind,
  OrderFiscalTaxationSystem,
  OrderFiscalVatRate,
  OrderPaymentMethod,
} from '@shared/api/graphql/generated';
import { inTimezone } from '@shared/lib/date-time';

/**
 * Коды KKMServer для доменных enum'ов. Таблицы объявлены как `Record<Enum, …>`,
 * поэтому новый кейс в схеме ломает компиляцию здесь, а не приводит к молчаливой
 * отправке неверного кода в ККТ.
 */
const TAXATION_VARIANT: Record<OrderFiscalTaxationSystem, number> = {
  [OrderFiscalTaxationSystem.UsnIncome]: 1,
};

const VAT_TAX_CODE: Record<OrderFiscalVatRate, number> = {
  [OrderFiscalVatRate.None]: -1,
};

// ФФД 1.2, тег 1212 (SignCalculationObject): 1 — товар, 2 — подакцизный товар,
// 4 — услуга, 32 — товар с кодом маркировки, 33 — подакцизный товар с кодом.
const PAYMENT_SUBJECT_CODE: Record<OrderFiscalPaymentSubject, number> = {
  [OrderFiscalPaymentSubject.Commodity]: 1,
  [OrderFiscalPaymentSubject.ExcisableCommodity]: 2,
  [OrderFiscalPaymentSubject.MarkedCommodity]: 32,
  [OrderFiscalPaymentSubject.MarkedExcisableCommodity]: 33,
  [OrderFiscalPaymentSubject.Service]: 4,
};

const PAYMENT_METHOD_CODE: Record<OrderFiscalPaymentMethod, number> = {
  [OrderFiscalPaymentMethod.FullPayment]: 4,
};

// Тег 1054: 0 — приход, 1 — возврат прихода; чек коррекции: 2 — коррекция прихода,
// 3 — коррекция возврата прихода (по сэмплам 2.3.16.40, задача 1 плана, проверка 6).
const TYPE_CHECK: Record<OrderFiscalReceiptKind, Record<OrderFiscalOperationType, number>> = {
  [OrderFiscalReceiptKind.Regular]: {
    [OrderFiscalOperationType.Settlement]: 0,
    [OrderFiscalOperationType.Refund]: 1,
  },
  [OrderFiscalReceiptKind.Correction]: {
    [OrderFiscalOperationType.Settlement]: 2,
    [OrderFiscalOperationType.Refund]: 3,
  },
};

export interface MapMorsReceiptPlanInput {
  acquiringIdCommand?: string;
  executionTarget: OrderFiscalExecutionTarget;
  idCommand: string;
  profile?: KkmServerProtocolProfile;
  receiptPlan: OrderFiscalReceiptPlan | OrderFiscalRefundReceiptPlan;
  /** Смена расчёта закрыта: печатается чек коррекции с датой расчёта `settledAt`. */
  receiptKind?: OrderFiscalReceiptKind;
  settledAt?: string;
}

export interface KkmServerMappedReceiptPlan {
  acquiringCommand: KkmServerCommand | null;
  acquiringDeviceKey: string | null;
  registerCheckCommand: KkmServerCommand;
  registerDeviceKey: string;
}

export function mapMorsReceiptPlan(input: MapMorsReceiptPlanInput): KkmServerMappedReceiptPlan {
  const profile = input.profile ?? KKM_SERVER_2_3_16_36_PROFILE;
  const plan = input.receiptPlan;

  assertCommonPlan(plan, input.idCommand);
  const orderedPayments = [...plan.payments].sort((left, right) => left.execution_order - right.execution_order);
  assertPaymentOrder(orderedPayments, input.idCommand);

  const cashPayment = orderedPayments.find((payment) => payment.method === OrderPaymentMethod.Cash);
  const cardPayment = orderedPayments.find((payment) => payment.method === OrderPaymentMethod.Card);
  const cashAmount = cashPayment?.amount ?? 0;
  const cardAmount = cardPayment?.amount ?? 0;
  const advanceAmount = plan.schema_version === 2 ? plan.advance_amount : 0;

  if (cashAmount + cardAmount + advanceAmount !== plan.total_amount) {
    unsupported(input.idCommand, 'Сумма способов оплаты не совпадает с чеком.');
  }

  const terminalDeviceId = input.executionTarget.terminal_device_id;
  let acquiringCommand: KkmServerCommand | null = null;
  let acquiringDeviceKey: string | null = null;

  // Возврат вне терминала (СБП в меню терминала, перевод) идёт картой в чеке, но без команды терминалу.
  if (cardPayment !== undefined && cardPayment.use_acquiring) {
    if (
      terminalDeviceId === null ||
      terminalDeviceId === undefined ||
      terminalDeviceId.trim() === '' ||
      input.acquiringIdCommand === undefined
    ) {
      unsupported(input.idCommand, 'Для безналичной части отсутствует terminal binding или correlation ID.');
    }

    acquiringCommand = mapAcquiringCommand(
      plan,
      cardPayment,
      terminalDeviceId,
      input.acquiringIdCommand,
      profile,
      input.idCommand,
    );
    acquiringDeviceKey = kkmServerDeviceKey('terminal', terminalDeviceId);
  }

  return {
    acquiringCommand,
    acquiringDeviceKey,
    registerCheckCommand: {
      Cash: toRubles(cashAmount),
      CashProvision: 0,
      CheckStrings: plan.items.map(mapReceiptItem),
      Command: 'RegisterCheck',
      Credit: 0,
      ElectronicPayment: toRubles(cardAmount),
      IdCommand: input.idCommand,
      IdDevice: input.executionTarget.kkt_device_id,
      IsFiscalCheck: true,
      NotPrint: false,
      NumDevice: 0,
      NumDeviceByProcessing: null,
      PayByProcessing: false,
      TaxVariant: kkmCode(TAXATION_VARIANT, plan.taxation_system, 'СНО чека не подтверждена protocol profile.'),
      Timeout: profile.timeouts.kkt.commandTimeoutSeconds,
      TypeCheck: TYPE_CHECK[input.receiptKind ?? OrderFiscalReceiptKind.Regular][plan.operation_type],
      AdvancePayment: toRubles(advanceAmount),
      CashierName: plan.cashier_name,
      ...receiptContact(plan),
      ...correctionFields(input),
    },
    registerDeviceKey: kkmServerDeviceKey('kkt', input.executionTarget.kkt_device_id),
  };
}

export function kkmServerDeviceKey(device: 'kkt' | 'terminal', deviceId: string): string {
  return `${device}:${deviceId}`;
}

/**
 * Класс таймаута протокола для устройства — пара к `kkmServerDeviceKey`: обе функции
 * отвечают на один и тот же вопрос «куда уходит команда», и вызываются вместе.
 * Команды смены просят класс `shift` явно: у них свой бюджет на той же ККТ.
 */
export function kkmServerTimeoutClass(device: 'kkt' | 'terminal'): KkmServerTimeoutClass {
  return device === 'terminal' ? 'acquiring' : 'kkt';
}

function assertCommonPlan(plan: OrderFiscalReceiptPlan | OrderFiscalRefundReceiptPlan, operationId: string): void {
  if (plan.schema_version !== 1 && plan.schema_version !== 2) {
    unsupported(operationId, 'Версия фискального плана не поддерживается. Обновите приложение.');
  }
  if (
    !Number.isSafeInteger(plan.advance_amount) ||
    plan.advance_amount < 0 ||
    (plan.schema_version === 1 && plan.advance_amount !== 0)
  ) {
    unsupported(operationId, 'Сумма зачёта предоплаты повреждена.');
  }
  if (
    plan.operation_type !== OrderFiscalOperationType.Settlement &&
    plan.operation_type !== OrderFiscalOperationType.Refund
  ) {
    console.warn('[kkm-receipt-mapper.unknown-operation-type]', {
      operationId,
      operationType: plan.operation_type,
    });
    unsupported(operationId, 'Тип фискальной операции не поддерживается.');
  }

  if (plan.currency !== OrderCurrency.Rub || plan.total_amount <= 0) {
    unsupported(operationId, 'Валюта, СНО или zero-money чек не подтверждены protocol profile.');
  }

  const itemTotal = plan.items.reduce((total, item) => total + item.amount, 0);

  if (itemTotal !== plan.total_amount || plan.items.length === 0) {
    unsupported(operationId, 'Строки чека не совпадают с итоговой суммой.');
  }
}

function assertPaymentOrder(
  payments: Array<OrderFiscalReceiptPlan['payments'][number] | OrderFiscalRefundReceiptPlan['payments'][number]>,
  operationId: string,
): void {
  const methods = payments.map((payment) => payment.method);

  if (
    new Set(methods).size !== methods.length ||
    methods.some((method) => method !== OrderPaymentMethod.Card && method !== OrderPaymentMethod.Cash)
  ) {
    unsupported(operationId, 'Payment mapping содержит неизвестный способ.');
  }

  const cardIndex = methods.indexOf(OrderPaymentMethod.Card);
  const cashIndex = methods.indexOf(OrderPaymentMethod.Cash);

  if (cardIndex >= 0 && cashIndex >= 0 && cardIndex > cashIndex) {
    unsupported(operationId, 'Split payment обязан выполнять карту перед наличными.');
  }
}

function mapAcquiringCommand(
  plan: OrderFiscalReceiptPlan | OrderFiscalRefundReceiptPlan,
  payment: OrderFiscalReceiptPlan['payments'][number] | OrderFiscalRefundReceiptPlan['payments'][number],
  terminalDeviceId: string,
  acquiringIdCommand: string,
  profile: KkmServerProtocolProfile,
  operationId: string,
): KkmServerCommand {
  const common = {
    Amount: toRubles(payment.amount),
    IdCommand: acquiringIdCommand,
    IdDevice: terminalDeviceId,
    NumDevice: 0,
    Timeout: profile.timeouts.acquiring.commandTimeoutSeconds,
  };

  if (plan.operation_type === OrderFiscalOperationType.Settlement) {
    return {
      ...common,
      Command: 'PayByPaymentCard',
    };
  }

  if (!isRefundPayment(payment)) {
    unsupported(operationId, 'Refund payment mapping повреждён.');
  }

  const rrn = payment.original_rrn?.trim();
  const universalId = payment.original_terminal_transaction_id?.trim();

  if (
    rrn === undefined ||
    rrn === '' ||
    universalId === undefined ||
    universalId === '' ||
    !universalId.toLocaleUpperCase('ru-RU').includes(`RRN:${rrn.toLocaleUpperCase('ru-RU')}`)
  ) {
    unsupported(operationId, 'Возврат на карту не содержит подтверждённый UniversalID с исходным RRN.');
  }

  return {
    ...common,
    Command: 'ReturnPaymentByPaymentCard',
    UniversalID: universalId,
  };
}

function mapReceiptItem(item: OrderFiscalReceiptItem): Record<string, unknown> {
  if (item.quantity <= 0 || !Number.isInteger(item.quantity) || item.amount < 0 || item.unit_price < 0) {
    unsupported(null, 'Строка чека содержит неподтверждённый VAT/ФФД mapping.');
  }

  const unmapped = 'Строка чека содержит неподтверждённый VAT/ФФД mapping.';

  return {
    Register: {
      Amount: toRubles(item.amount),
      Department: 0,
      // Тег 1030 — не более 64 символов; срез по code point не рвёт surrogate pair.
      // Полное название остаётся в title_snapshot и frozen payload заказа.
      Name: Array.from(item.title).slice(0, 64).join(''),
      Price: toRubles(item.unit_price),
      Quantity: item.quantity,
      SignCalculationObject: kkmCode(PAYMENT_SUBJECT_CODE, item.payment_subject, unmapped),
      SignMethodCalculation: kkmCode(PAYMENT_METHOD_CODE, item.payment_method, unmapped),
      Tax: kkmCode(VAT_TAX_CODE, item.vat_rate, unmapped),
      ...markingFields(item),
    },
  };
}

/**
 * Поля кода маркировки строки (тег 1163 в ФФД 1.2, `GoodCodeData` на входе KKMServer).
 *
 * `AcceptOnBad: false` запрещает печать чека с непрошедшим проверку кодом: отказ
 * происходит до печати и классифицируется как «не выполнено». Поля нет в сэмплах
 * 2.3.16.40 — лишнее поле сервер игнорирует, реальное закрывает печать; статус
 * подтверждается стендом (см. `kkmserver-api-commands.md`).
 *
 * `MeasureOfQuantity: 0` (штуки) шлём только у строк с кодом: некоторые ККТ этого
 * требуют при передаче кода маркировки. У строк без кода поле не отправляется.
 */
function markingFields(item: OrderFiscalReceiptItem): Record<string, unknown> {
  const code = item.marking_code;

  if (code === null || code === undefined || code === '') {
    return {};
  }

  if (item.quantity !== 1) {
    console.warn('[kkm-receipt-mapper.marked-line-quantity]', {
      quantity: item.quantity,
      orderLineId: item.order_line_id,
    });
    unsupported(null, 'Строка с кодом маркировки обязана содержать одну единицу товара.');
  }

  return {
    GoodCodeData: {
      AcceptOnBad: false,
      BarCode: code,
      ContainsSerialNumber: true,
    },
    MeasureOfQuantity: 0,
  };
}

/**
 * Runtime-страховка на случай, если бэкенд отдал кейс раньше обновления codegen.
 */
function kkmCode<T extends string>(table: Record<T, number>, key: T, safeMessage: string): number {
  const code: number | undefined = (table as Record<string, number | undefined>)[key];

  if (code === undefined) {
    unsupported(null, safeMessage);
  }

  return code;
}

function receiptContact(plan: OrderFiscalReceiptPlan | OrderFiscalRefundReceiptPlan): Record<string, unknown> {
  if (!('contact' in plan)) {
    return {};
  }

  const clientAddress = plan.contact.email ?? plan.contact.phone;

  return clientAddress === null || clientAddress === undefined ? {} : { ClientAddress: clientAddress };
}

/** Чек коррекции самостоятельный (`CorrectionType: 0`), основание — дата расчёта по местному времени ККТ. */
function correctionFields(input: MapMorsReceiptPlanInput): Record<string, unknown> {
  if (input.receiptKind !== OrderFiscalReceiptKind.Correction) {
    return {};
  }

  const settledAt = input.settledAt === undefined ? null : inTimezone(input.settledAt, input.executionTarget.timezone);

  if (settledAt === null) {
    unsupported(input.idCommand, 'Для чека коррекции нет даты расчёта.');
  }

  return {
    CorrectionType: 0,
    CorrectionBaseDate: settledAt.toPlainDateTime().toString({ smallestUnit: 'second' }),
  };
}

function isRefundPayment(
  payment: OrderFiscalReceiptPlan['payments'][number] | OrderFiscalRefundReceiptPlan['payments'][number],
): payment is OrderFiscalRefundPayment {
  return 'payment_id' in payment;
}

export function toRubles(kopecks: number): number {
  if (!Number.isSafeInteger(kopecks)) {
    unsupported(null, 'Денежное значение не является целым числом копеек.');
  }

  return Number((kopecks / 100).toFixed(2));
}

function unsupported(operationId: string | null, safeMessage: string): never {
  throw new KkmServerAdapterError({
    code: 'unsupported_mapping',
    command: 'RegisterCheck',
    operationId,
    outcome: 'known_not_run',
    safeMessage,
  });
}
